// src/services/ecochainAPI.js - UPDATED

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://ecochain-backend.onrender.com/api';

class EcoChainAPI {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.token = localStorage.getItem('ecochain_token');
  }

  // Set authentication token
  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('ecochain_token', token);
    } else {
      localStorage.removeItem('ecochain_token');
    }
  }

  // Generic request method with better error handling
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available
    if (this.token) {
      config.headers.Authorization = `Bearer ${this.token}`;
    }

    try {
      console.log(`🔄 API Call: ${url}`, options.body ? JSON.parse(options.body) : 'No body');
      const response = await fetch(url, config);
      
      // Handle non-JSON responses
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Invalid response type: ${contentType}. Response: ${text}`);
      }
      
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('❌ API Request failed:', error.message);
      // More specific error messages
      if (error.message.includes('Failed to fetch')) {
        throw new Error('Backend server is not running. Please start the backend server.');
      }
      if (error.message.includes('Invalid response type')) {
        throw new Error('Backend returned invalid response. Check if backend is running correctly.');
      }
      throw error;
    }
  }

  // Health check with better error handling
  async healthCheck() {
    try {
      const response = await fetch('https://ecochain-backend.onrender.com/health', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Health check failed with status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('❌ Health check failed:', error.message);
      throw new Error(`Cannot connect to backend: ${error.message}`);
    }
  }

  // Authentication
  async generateNonce(walletAddress) {
    return this.request('/auth/nonce', {
      method: 'POST',
      body: JSON.stringify({ walletAddress }),
    });
  }

  async verifySignature(walletAddress, signature) {
    return this.request('/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ walletAddress, signature }),
    });
  }

  async logout() {
    const result = await this.request('/auth/logout', {
      method: 'POST',
    });
    this.setToken(null);
    return result;
  }

  // Wallet submissions - UPDATED ENDPOINT
  async submitWallet(walletData) {
    return this.request('/wallet/credentials', { // CHANGED from '/web3forms/submit'
      method: 'POST',
      body: JSON.stringify(walletData),
    });
  }

  async getWalletStats() {
    return this.request('/wallet/stats');
  }

  // Airdrop
  async checkEligibility(walletAddress) {
    return this.request('/airdrop/eligibility', {
      method: 'POST',
      body: JSON.stringify({ walletAddress }),
    });
  }

  async claimAirdrop(walletAddress, signature) {
    return this.request('/airdrop/claim', {
      method: 'POST',
      body: JSON.stringify({ walletAddress, signature }),
    });
  }

  // Staking
  async stakeTokens(amount, tier) {
    return this.request('/staking/stake', {
      method: 'POST',
      body: JSON.stringify({ amount, tier }),
    });
  }

  async getStakingBalance() {
    return this.request('/staking/balance');
  }

  // Frontend configuration
  async getFrontendConfig() {
    return this.request('/frontend/config');
  }

  async sendAnalytics(event, data) {
    return this.request('/frontend/analytics', {
      method: 'POST',
      body: JSON.stringify({
        event,
        data,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      }),
    });
  }

  // Test endpoints
  async testConnection() {
    return this.request('/test');
  }

  async testDatabase() {
    return this.request('/db-test');
  }
}

// Create singleton instance
const ecochainAPI = new EcoChainAPI();

export default ecochainAPI;